import { Directive, ElementRef, Input, OnInit, OnDestroy, OnChanges, DoCheck, AfterViewInit, Renderer2, SimpleChanges, NgZone } from '@angular/core';
import { Router, NavigationEnd, Event } from '@angular/router';
import { filter } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { GuideService } from 'src/app/services/guide.service';

@Directive({
    selector: '[guide]'
})
export class GuideDirective implements OnInit, OnDestroy, OnChanges, DoCheck, AfterViewInit {
    @Input() guide?: string | null;

    private sub?: Subscription;
    private lastGuideKey: string | null = null;
    private lastGuideId: string | null = null;
    private lastRoute: string | null = null;

    constructor(
        private el: ElementRef,
        private renderer: Renderer2,
        private router: Router,
        private guideService: GuideService,
        private ngZone: NgZone
    ) { }

    ngOnInit() {
        this.ngZone.runOutsideAngular(() => {
            // Update on route change
            this.sub = this.router.events.pipe(
                filter((e: Event) => e instanceof NavigationEnd)
            ).subscribe(() => {
                this.updateAttributes(true);
            });
        });
    }

    ngAfterViewInit() {
        // Initial setup - wait for microtask to ensure [attr.guide] is applied if dynamic
        Promise.resolve().then(() => this.updateAttributes());
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['guide']) {
            this.updateAttributes();
        }
    }

    ngDoCheck() {
        // Light check: mostly rely on mutation observer or input changes
        if (!this.el.nativeElement || typeof this.el.nativeElement.getAttribute !== 'function') return;
        const currentAttr = this.el.nativeElement.getAttribute('guide');
        // Only trigger update if attribute mismatches last known ID and input is undefined (attr usage)
        if (this.guide === undefined && currentAttr !== this.lastGuideId) {
            this.updateAttributes();
        }
    }

    ngOnDestroy() {
        if (this.lastGuideKey) {
            this.guideService.unregister(this.lastGuideKey);
        }
        this.sub?.unsubscribe();
    }

    private updateAttributes(forceRouteCheck = false) {
        if (!this.el || !this.el.nativeElement || typeof this.el.nativeElement.getAttribute !== 'function') return;

        // Determine effective guide ID
        // Priority: @Input() guide > getAttribute('guide')
        let guideId = this.guide;

        // If @Input is not set (undefined or null), fallback to attribute
        if (guideId === undefined || guideId === null) {
            guideId = this.el.nativeElement.getAttribute('guide');
        }

        // Cleanup if no ID
        if (!guideId) {
            if (this.lastGuideKey) {
                this.guideService.unregister(this.lastGuideKey);
                this.lastGuideKey = null;
                this.renderer.removeAttribute(this.el.nativeElement, 'data-guide-key');
            }
            this.lastGuideId = null;
            return;
        }

        // Use SERVICE provided normalization to ensure perfect match
        const currentRoute = this.guideService.normalizeRoute(this.router.url);
        const newKey = `${currentRoute}::${guideId}`;

        // Optimization: only update if changed
        if (!forceRouteCheck && guideId === this.lastGuideId && currentRoute === this.lastRoute && newKey === this.lastGuideKey) {
            return;
        }

        // Optimization: if key is same, don't unregister/register, just update props
        if (this.lastGuideKey && this.lastGuideKey !== newKey) {
            this.guideService.unregister(this.lastGuideKey);
        }

        this.lastGuideId = guideId;
        this.lastRoute = currentRoute;
        this.lastGuideKey = newKey;

        // Run DOM updates outside Angular zone
        this.ngZone.runOutsideAngular(() => {
            // 1. Ensure 'guide' attribute matches ID (for querySelector)
            const existingAttr = this.el.nativeElement.getAttribute('guide');
            if (existingAttr !== guideId) {
                this.renderer.setAttribute(this.el.nativeElement, 'guide', guideId);
            }

            // 2. Set 'data-guide-key'
            this.renderer.setAttribute(this.el.nativeElement, 'data-guide-key', newKey);

            // 3. Register with Service in Registry
            this.guideService.register(newKey, this.el.nativeElement);
        });
    }
}
